import React from 'react';
import {
  BookOpenCheck, MessageSquareText, PenTool, ShieldCheck, Sparkles, Target,
} from 'lucide-react';
import Reveal from './Reveal';

const secondary = [
  {
    icon: MessageSquareText,
    title: 'Message between lessons',
    body: 'Stuck on question 7 at 9pm? Send it over. Tutors reply in the same thread you booked in.',
  },
  {
    icon: Target,
    title: 'Goal-oriented plans',
    body: 'Every student gets a written plan tied to their target grade, reviewed at the end of each half term.',
  },
  {
    icon: ShieldCheck,
    title: 'Safeguarding by default',
    body: 'DBS checks, recorded sessions and parent visibility on every lesson. Nothing happens off-platform.',
  },
  {
    icon: BookOpenCheck,
    title: 'Exam board aware',
    body: 'AQA, Edexcel, OCR and WJEC specs mapped question by question, so revision matches the real paper.',
  },
];

const FeatureGrid: React.FC = () => (
  <section className="relative isolate overflow-hidden bg-base-200/50 py-20 sm:py-28">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-1/2 top-0 -z-10 h-96 w-[48rem] -translate-x-1/2 rounded-full bg-brand-500/10 blur-[120px]"
    />

    <div className="section-shell">
      <Reveal className="max-w-2xl">
        <p className="eyebrow">The platform</p>
        <h2 className="display mt-3 text-4xl text-balance sm:text-5xl">
          Built for teaching, not just
          {' '}
          <span className="italic text-brand-600 dark:text-brand-400">video calls</span>
        </h2>
        <p className="mt-5 text-lg text-base-content/60">
          Most tutoring happens over a generic meeting link and a shared PDF. We built the
          classroom instead.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        {/* Feature spotlight */}
        <Reveal className="lg:col-span-2">
          <div className="surface relative h-full overflow-hidden p-8 shadow-soft sm:p-10">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-600 text-white shadow-glow">
              <PenTool className="h-5 w-5" />
            </span>
            <h3 className="mt-6 text-2xl font-semibold">A whiteboard that keeps up</h3>
            <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-base-content/60">
              Draw the working, not just the answer. Infinite canvas, LaTeX, graph plotting and
              past-paper import — all saved to the lesson so it can be revisited the night before
              the exam.
            </p>

            {/* Miniature canvas illustration */}
            <div className="relative mt-8 overflow-hidden rounded-xl border border-ink-900/[0.08] bg-base-100 dark:border-white/10">
              <div className="flex items-center gap-1.5 border-b border-ink-900/[0.07] px-3 py-2 dark:border-white/10">
                {['Pen', 'Shapes', 'LaTeX', 'Graph'].map((tool, i) => (
                  <span
                    key={tool}
                    className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                      i === 0
                        ? 'bg-brand-600 text-white'
                        : 'text-base-content/50'
                    }`}
                  >
                    {tool}
                  </span>
                ))}
              </div>
              <div className="relative h-40 bg-grid-light bg-grid dark:bg-grid-dark">
                <svg
                  aria-hidden="true"
                  viewBox="0 0 400 160"
                  className="absolute inset-0 h-full w-full"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M20 140 C 90 140, 120 30, 200 30 S 320 130, 380 26"
                    fill="none"
                    stroke="currentColor"
                    className="text-brand-500"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle cx="200" cy="30" r="5" className="fill-amber-400" />
                </svg>
                <span className="absolute bottom-3 left-4 rounded-md bg-base-100/90 px-2 py-1 font-mono text-[11px] text-base-content/70 shadow-soft">
                  y = x² − 4x + 7
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* AI recap */}
        <Reveal delay={0.1}>
          <div className="surface flex h-full flex-col p-8 shadow-soft">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-amber-400 text-ink-950 shadow-soft">
              <Sparkles className="h-5 w-5" />
            </span>
            <h3 className="mt-6 text-2xl font-semibold">Recaps that write themselves</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-base-content/60">
              Minutes after the lesson ends, students and parents get a summary of what was
              covered, where the gaps are, and three questions to try before next time.
            </p>
            <div className="mt-auto space-y-2 pt-8">
              {['Covered: completing the square', 'Gap: sign errors when factorising', 'Set: 3 questions, due Thu'].map(
                (line) => (
                  <div
                    key={line}
                    className="rounded-lg border border-ink-900/[0.07] bg-base-200/60 px-3 py-2 text-xs text-base-content/65 dark:border-white/10"
                  >
                    {line}
                  </div>
                ),
              )}
            </div>
          </div>
        </Reveal>

        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-4">
          {secondary.map((feature, i) => (
            <Reveal key={feature.title} delay={0.05 * i}>
              <div className="surface h-full p-7 shadow-soft transition duration-300 hover:-translate-y-1 hover:shadow-lift">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-brand-600/10 text-brand-600 ring-1 ring-inset ring-brand-600/15 dark:text-brand-400">
                  <feature.icon className="h-4 w-4" />
                </span>
                <h3 className="mt-5 text-base font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-base-content/60">{feature.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default FeatureGrid;
